// File: theme_mode_provider.tsx
// Description: Provides the ThemeModeContext value and wraps children with MUI's ThemeProvider (built from the current mode) and CssBaseline.

import { useState, useMemo } from "react";
import { createTheme } from "@mui/material/styles";
import { themeSettings } from "@/constants/theme_settings"
import { ThemeProvider, CssBaseline } from '@mui/material';
import { ThemeModeContext } from "@/context/theme/theme_mode_context"
import type { PropsWithChildren } from 'react';

/**
 * Context provider that exposes the current color mode and lets descendants
 * toggle it, while wrapping children with MUI's ThemeProvider/CssBaseline.
 *
 * @param {PropsWithChildren} props.children - Components that will have access to the color mode.
 * @returns {JSX.Element}
 */
function ThemeModeProvider({ children }: PropsWithChildren) {

  // Defaults to light mode
  const [mode, setMode] = useState("light");

  // Memorised so consumers relying on this reference (e.g. useMemo/useCallback deps)
  // don't re-render on every ThemeModeProvider render
  const colorModeValue = useMemo(
    () => ({
      mode,
      toggleColorMode: () => {
        const next = mode === "dark" ? "light" : "dark";
        setMode(next);
        return next;
      },
    }),[mode]
  );

  // Memorised so the theme is only rebuilt when the mode actually changes
  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);

  return (
    <ThemeModeContext.Provider value={colorModeValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export default ThemeModeProvider